import i18n from '@/utils/i18n';
import React from 'react';
import { BORDER_RADIUS_SMALL, TYPOGRAPHY } from '@/styles';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_CURRENCY_CODE, formatCurrency } from '@/utils/money';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { OrderableItemListItemImage } from '@/components/orders/orderable-item-list-item-image';
import { SampleRequestVariantPackFieldsFragment } from '@/types';
import { StyleProp, Text, View, ViewStyle } from 'react-native';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useItemDisplayOptions } from '@/contexts/new-order-context';
import { useSmallLayoutSizeValue } from '@/contexts/layout-context';

interface Props {
  metadata: ItemListOptionMetadata;
  onAddToOrderGuidePress: () => void;
  onSkipPress: () => void;
  sampleRequest: SampleRequestVariantPackFieldsFragment;
}

export const SampleRequestFollowUpAddToOrderGuide: React.FC<Props> = ({
  metadata,
  onAddToOrderGuidePress,
  onSkipPress,
  sampleRequest,
}) => {
  const { advertiser_name: advertiserName, variant_pack: variantPack } =
    sampleRequest;
  const { uuid: sampleRequestUUID } = sampleRequest?.sample_request || {};
  const [styles] = useStyles();
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const { hidePlaceholderImages } = useItemDisplayOptions();

  const trackingProperties = {
    restaurantUUID,
    sampleRequestUUID,
    supplierUUID,
  };

  const buttonsWrapperStyle = useSmallLayoutSizeValue<StyleProp<ViewStyle>>(
    null,
    styles.buttonsHorizontal,
  );

  const buttonsStyle = useSmallLayoutSizeValue<StyleProp<ViewStyle>>(
    null,
    styles.flexOne,
  );
  const altText = [metadata.displayName, metadata.externalID]
    .filter(Boolean)
    .join('-');

  return (
    <>
      <View style={styles.requestedSample}>
        <OrderableItemListItemImage
          alt={altText}
          hidePlaceholderImages={hidePlaceholderImages}
          imageSize={56}
          imageURLs={metadata.photoURLs}
          style={styles.image}
        />
        <View style={styles.flexOne}>
          <Text
            style={[TYPOGRAPHY.pBold, styles.smallText, styles.advertiserName]}
          >
            {advertiserName}
          </Text>
          <Text numberOfLines={1} style={[TYPOGRAPHY.pBold, styles.smallText]}>
            {variantPack?.item.display_name}
          </Text>
          {metadata.unitPriceInMicros ? (
            <View style={styles.pricePerUnit}>
              <Text style={[TYPOGRAPHY.pBold, styles.smallText]}>
                {formatCurrency(
                  metadata.unitCount * metadata.unitPriceInMicros,
                  metadata.currencyCode || DEFAULT_CURRENCY_CODE,
                )}
              </Text>
              <Text
                style={[TYPOGRAPHY.p, styles.smallText]}
              >{`/${metadata.packDisplayString}`}</Text>
            </View>
          ) : null}
        </View>
      </View>
      <View style={[buttonsWrapperStyle, styles.buttonsWithGap]}>
        <Button
          containerStyle={buttonsStyle}
          onPress={onAddToOrderGuidePress}
          trackingData={{
            name: 'sample_request_follow_up.add_sample_to_og',
            properties: trackingProperties,
          }}
        >
          {i18n.t('add_to_order_guide_label')}
        </Button>
        <Button
          containerStyle={buttonsStyle}
          onPress={onSkipPress}
          trackingData={{
            name: 'sample_request_follow_up.skip_add_sample_to_og',
            properties: trackingProperties,
          }}
          type="secondary"
        >
          {i18n.t('skip')}
        </Button>
      </View>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  advertiserName: {
    color: theme.colors.inactiveText,
    textTransform: 'uppercase',
  },
  buttonsHorizontal: {
    flexDirection: 'row',
  },
  buttonsWithGap: {
    gap: 16,
  },
  flexOne: {
    flex: 1,
  },
  image: {
    borderRadius: BORDER_RADIUS_SMALL,
  },
  pricePerUnit: {
    flexDirection: 'row',
  },
  requestedSample: {
    alignItems: 'center',
    backgroundColor: theme.colors.background,
    borderRadius: BORDER_RADIUS_SMALL,
    flexDirection: 'row',
    gap: 16,
    marginBottom: 24,
    paddingHorizontal: 12,
    paddingVertical: 16,
  },
  smallText: {
    fontSize: theme.fontSizes.small,
  },
}));
